import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { UtensilsCrossed, GlassWater, Armchair } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PreviewRibbon } from "@/components/cef/cefShared";

/* Stone Overlay CEF — /cef/stone/:serverId — a transparent full-screen layer the
   engine keeps on top while the player is high. Polls /grow/stone and applies the
   server-issued tier's visual grade: saturation boost, breathing, fisheye vignette,
   warm hue pulse, edge tunnel, paranoia ticks, whitey desaturation + flicker.
   Eat/drink buttons sober you faster. Everything fades with the server clock. */

const TIER_STYLE = (tier, phase) => {
  switch (tier) {
    case 1: return { filter: `saturate(${1.1 + phase * 0.08}) brightness(1.02)`, vignette: 0 };
    case 2: return { filter: `saturate(1.3) blur(${0.4 + phase * 0.4}px)`, vignette: 0.15, fisheye: true };
    case 3: return { filter: `saturate(1.45) hue-rotate(${12 + phase * 10}deg) sepia(0.18)`, vignette: 0.3, pulse: true, wobble: true };
    case 4: return { filter: `saturate(1.15) hue-rotate(18deg) contrast(1.05) brightness(${0.82 - phase * 0.06})`, vignette: 0.55, tunnel: true, paranoia: true };
    case 5: return { filter: `saturate(${0.25 + phase * 0.15}) brightness(${0.68 + phase * 0.08})`, vignette: 0.8, flicker: true, mustRest: true };
    default: return null;
  }
};

export default function StoneCefPage() {
  const { serverId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const demo = !token;
  const [stone, setStone] = useState(null);
  const [phase, setPhase] = useState(0); // breathing phase 0..1
  const [resting, setResting] = useState(false);

  useEffect(() => {
    document.documentElement.style.background = "transparent";
    document.body.style.background = "transparent";
    return () => { document.documentElement.style.background = ""; document.body.style.background = ""; };
  }, []);

  const poll = useCallback(() => {
    if (demo) { setStone((s) => s || { tier: parseInt(params.get("tier") || "3"), label: "High", seconds_left: 600 }); return; }
    api.get(`/nc/servers/${serverId}/grow/stone`, { params: { token } })
      .then((r) => setStone(r.data.stone?.tier ? r.data.stone : null))
      .catch(() => {});
  }, [demo, serverId, token, params]);

  useEffect(() => { poll(); const t = setInterval(poll, 15000); return () => clearInterval(t); }, [poll]);
  useEffect(() => { // breathing phase
    let raf; const t0 = performance.now();
    const tickFn = () => { setPhase((Math.sin((performance.now() - t0) / 1400) + 1) / 2); raf = requestAnimationFrame(tickFn); };
    raf = requestAnimationFrame(tickFn); return () => cancelAnimationFrame(raf);
  }, []);

  const sober = async (what) => {
    if (demo) { toast.success(`+${what === "ate" ? "4" : "2"} min sobered (preview)`); return; }
    try {
      const r = await api.post(`/nc/servers/${serverId}/grow/sober`, { ate: what === "ate", drank: what === "drank" }, { params: { token } });
      setStone(r.data.stone?.tier ? r.data.stone : null);
      toast.success(r.data.message);
    } catch { toast.error("Couldn't reach the server"); }
  };

  const tier = stone?.tier || 0;
  if (!tier) return <div data-testid="stone-sober" className="min-h-screen bg-transparent" />;
  const st = TIER_STYLE(tier, phase);
  const mins = Math.max(0, Math.ceil((stone.seconds_left || 0) / 60));

  return (
    <div data-testid="stone-overlay" data-tier={tier} className="fixed inset-0 pointer-events-none" style={{ zIndex: 5 }}>
      <style>{`
        @keyframes stoneFlicker { 0%, 92% { opacity: 0; } 94% { opacity: 0.85; } 96% { opacity: 0.1; } 98% { opacity: 0.7; } 100% { opacity: 0; } }
        @keyframes stonePulse { 0%,100% { opacity: 0.12; } 50% { opacity: 0.3; } }
        @keyframes paranoidTick { 0%, 88% { opacity: 0; transform: translateX(0); } 92% { opacity: 0.5; transform: translateX(-6px); } 96% { opacity: 0.3; transform: translateX(4px); } 100% { opacity: 0; } }
      `}</style>
      {/* the grade itself — a filter layer the 3D world shows through */}
      <div className="absolute inset-0" style={{ backdropFilter: st.filter, WebkitBackdropFilter: st.filter }} />
      {/* vignette / tunnel */}
      {st.vignette > 0 && (
        <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse ${st.tunnel ? "55%" : "75%"} ${st.tunnel ? "50%" : "70%"} at center, transparent 40%, rgba(0,0,0,${st.vignette}) 100%)` }} />
      )}
      {/* tier 3 warm pulse */}
      {st.pulse && <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at center, rgba(190,220,60,0.08), transparent 70%)", animation: "stonePulse 3.2s ease-in-out infinite" }} />}
      {/* tier 4 paranoia — something at the edge of vision, gone when you look */}
      {st.paranoia && (
        <div className="absolute top-1/3 right-[8%] w-10 h-24 bg-black/80 rounded-t-full" style={{ animation: "paranoidTick 7s ease-in-out infinite", filter: "blur(2px)" }} />
      )}
      {/* tier 5 whitey — black flickers at the edges */}
      {st.flicker && <div className="absolute inset-0 bg-black" style={{ animation: "stoneFlicker 5.5s linear infinite" }} />}

      {/* HUD chip + sober controls (pointer events only here) */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 pointer-events-auto flex items-center gap-2 border border-white/15 bg-black/80 backdrop-blur-md px-4 py-2.5" data-testid="stone-hud">
        <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: tier >= 4 ? "#ef4444" : tier === 3 ? "#f59e0b" : "#4ade80" }} />
        <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-300">{stone.label} · tier {tier}/5 · ~{mins}m left</span>
        <button data-testid="stone-eat" onClick={() => sober("ate")} className="ml-2 px-2.5 py-1.5 font-mono text-[8px] uppercase tracking-[0.15em] border border-white/15 text-zinc-300 hover:border-white/40 transition-colors flex items-center gap-1"><UtensilsCrossed className="w-3 h-3" /> Eat</button>
        <button data-testid="stone-drink" onClick={() => sober("drink")} className="px-2.5 py-1.5 font-mono text-[8px] uppercase tracking-[0.15em] border border-white/15 text-zinc-300 hover:border-white/40 transition-colors flex items-center gap-1"><GlassWater className="w-3 h-3" /> Drink</button>
        {st.mustRest && (
          <button data-testid="stone-rest" onClick={() => setResting((r) => !r)}
            className={`px-2.5 py-1.5 font-mono text-[8px] uppercase tracking-[0.15em] border transition-colors flex items-center gap-1 ${resting ? "border-emerald-400/60 text-emerald-300" : "border-red-500/50 text-red-300 animate-pulse"}`}>
            <Armchair className="w-3 h-3" /> {resting ? "Riding it out…" : "Sit down. NOW."}
          </button>
        )}
      </div>
      {st.mustRest && !resting && (
        <div data-testid="stone-whitey-warning" className="absolute top-16 inset-x-0 flex justify-center pointer-events-none">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-red-300 bg-black/80 border border-red-500/40 px-4 py-2 animate-pulse">You're going pale — sit or lie down before you drop</p>
        </div>
      )}
      {demo && <PreviewRibbon what="stone overlay" />}
    </div>
  );
}
